import { inject, Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { AppEvent } from '../models/app-event.models';
import { SSE_CONFIG } from './provide-sse';

interface RequestObservableOptions {
  messageEvent?: string;
  urlRefresh?: () => string;
}
interface SSERequest {
  url: string;
  subject: Subject<AppEvent>;
  messageEvent: string;
  eventSource?: EventSource;
  status: 'Open' | 'Closed' | 'Connecting';
  urlRefresh?: () => string;
}
interface SSERequestOptions {
  subject?: Subject<AppEvent>;
  messageEvent?: string;
  urlRefresh?: () => string;
}

@Injectable({
  providedIn: 'root',
})
export class SSEService {
  private readonly config = inject(SSE_CONFIG, { optional: true });
  private readonly requests: Record<string, SSERequest> = {};

  /** Builds a new SSE request for a given URL */
  private buildSSERequest(url: string, options: SSERequestOptions = {}): SSERequest {
    const messageEvent = options.messageEvent || 'message';
    const subject = options.subject || new Subject<AppEvent>();
    const request: SSERequest = {
      url,
      subject,
      messageEvent,
      status: 'Connecting',
      urlRefresh: options.urlRefresh,
    };
    this.connectEventSource(request);
    return request;
  }
  /** Create and connect event source to request, sets up callbacks */
  private connectEventSource(request: SSERequest) {
    if (request.status === 'Closed') return;
    request.status = 'Connecting';
    request.eventSource = new EventSource(request.url);
    request.eventSource.addEventListener(request.messageEvent, (m) => this.onMessage(request, m));
    request.eventSource.addEventListener('error', (_) => this.onError(request));
    request.eventSource.addEventListener('open', (_) => this.onOpen(request));
  }
  /** Handles event source message, send to subject */
  private onMessage(request: SSERequest, message: MessageEvent<string>) {
    const data = JSON.parse(message.data);
    request.subject.next(data);
  }
  /** Handles event source error. If event source has been closed try to reconnect */
  private onError(request: SSERequest) {
    if (
      request.status !== 'Closed' &&
      request.eventSource &&
      request.eventSource.readyState === request.eventSource.CLOSED
    ) {
      setTimeout(() => {
        this.refreshRequestUrl(request);
        this.connectEventSource(request);
      }, 3000);
    }
  }
  /** Handles event source open, set status */
  private onOpen(request: SSERequest) {
    request.status = 'Open';
  }
  /** Rebuilds the EventSource URL from current cursor when a resolver is set */
  private refreshRequestUrl(request: SSERequest): void {
    if (!request.urlRefresh || request.status === 'Closed') return;
    const nextUrl = request.urlRefresh();
    if (nextUrl === request.url) return;
    delete this.requests[request.url];
    request.url = nextUrl;
    this.requests[nextUrl] = request;
  }
  /** Closes SSE request closing event source, setting status and deleting */
  private closeRequest(request: SSERequest) {
    if (request.eventSource && request.eventSource.readyState !== request.eventSource.CLOSED)
      request.eventSource.close();
    request.status = 'Closed';
    delete this.requests[request.url];
  }
  /**
   * Builds observable for a given URL
   * Attaches to existing request if exists, initialize request if it doesn't
   * Closes event source on all observers unsubscribe
   *
   * @param url - Full url of SSE request
   * @returns observable emitting async data from request
   */
  private buildRequestObservable(
    url: string,
    options: RequestObservableOptions = {},
  ): Observable<AppEvent> {
    if (!this.requests[url]) {
      const messageEvent = options.messageEvent || 'message';
      this.requests[url] = this.buildSSERequest(url, {
        messageEvent,
        urlRefresh: options.urlRefresh,
      });
    }
    const request = this.requests[url];
    return new Observable((observer) => {
      const sub = request.subject.subscribe((e) => observer.next(e));
      return () => {
        sub.unsubscribe();
        if (!request.subject.observed) this.closeRequest(request);
      };
    });
  }
  /**
   * Create SSE request to get new alarms for eventId
   *
   * @param path - url of SSE endpoint
   * @param [messageEvent='message'] - expected event string
   */
  public listen$(url: string, messageEvent: string = 'message'): Observable<AppEvent> {
    return this.buildRequestObservable(url, { messageEvent });
  }
  /** Generate App Events URL based on last processed event */
  private generateAppEventsUrl(): string {
    const config = this.config!;
    const stored = localStorage.getItem(config.cursorStorageKey);
    const lastEventId = stored ? stored : null;
    return config.url(lastEventId);
  }
  /**
   * Requests App Event SSE, locked to one connection per user
   *
   * @param cb - event callback
   * @returns close function, closes app event sse
   */
  public requestAppEventsSSE(cb: (event: AppEvent) => void): () => void {
    const config = this.config;
    if (!config) {
      return () => undefined;
    }
    const abortController = new AbortController();
    let close = () => abortController.abort();
    navigator.locks.request(
      config.lockName,
      { signal: abortController.signal },
      async () =>
        new Promise((resolve) => {
          const url = this.generateAppEventsUrl();
          const sub = this.buildRequestObservable(url, {
            urlRefresh: () => this.generateAppEventsUrl(),
          }).subscribe((event) => {
            if (event.id) localStorage.setItem(config.cursorStorageKey, event.id);
            cb(event);
          });
          close = () => {
            sub.unsubscribe();
            abortController.abort();
            resolve(null);
          };
        }),
    );
    return () => close();
  }
}
